import React, { useState } from "react";
import styled from "styled-components";
import { Helmet } from "react-helmet";
import axios from "axios";
import {
  FaSearch,
  FaTruck,
  FaMapMarkerAlt,
  FaSpinner,
  FaPrint,
  FaUser,
  FaBuilding,
  FaBox,
  FaBoxOpen,
  FaPlayCircle,
} from "react-icons/fa";
import { BASE_URL } from "../../services/authServices";

const formatAddress = (addr) => {
  const parts = [addr?.city, addr?.state, addr?.country].filter(Boolean);
  return parts.length ? parts.join(", ") : "—";
};

const formatDate = (iso) =>
  new Date(iso).toLocaleDateString("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
  });

const formatDateTime = (iso) =>
  new Date(iso).toLocaleString("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });

const calcProgress = (sentDate, estimatedDeliveryDate) => {
  const start = new Date(sentDate).getTime();
  const end = new Date(estimatedDeliveryDate).getTime();
  const now = Date.now();

  if (now <= start) return 2;
  if (now >= end) return 100;

  const total = end - start;
  const elapsed = now - start;
  return Math.min(Math.max(Math.round((elapsed / total) * 100), 2), 98);
};

const TrackShipment = () => {
  const [trackingNumber, setTrackingNumber] = useState("");
  const [isTracking, setIsTracking] = useState(false);
  const [shipment, setShipment] = useState(null);
  const [error, setError] = useState("");

  const handleSearch = async (e) => {
    e.preventDefault();
    if (!trackingNumber.trim()) {
      setError("Please enter a tracking number");
      return;
    }

    setIsTracking(true);
    setError("");
    setShipment(null);

    try {
      const { data } = await axios.get(
        `${BASE_URL}/api/user/shipment/track/${trackingNumber.trim()}`,
      );

      if (data.success && data.data) {
        setShipment(data.data);
      } else {
        setError(data.message || "No shipment found with this tracking number");
      }
    } catch (err) {
      const message =
        err?.response?.data?.message ||
        err?.message ||
        "No shipment found with this tracking number";
      setError(message);
    } finally {
      setIsTracking(false);
    }
  };

  const handlePrint = () => {
    if (!shipment) return;

    const held = shipment.isPaused;
    const statusColor = held ? "#e53e3e" : getProgressColor();
    const fmtAddr = (a) =>
      a
        ? `${a.street ? `${a.street}, ` : ""}${a.city}, ${a.state} ${a.postalCode}, ${a.country}`
        : "";

    const historyRows = [...shipment.trackingHistory]
      .reverse()
      .map((item) => {
        const isHeld = item.eventType === "pause";
        const isReleased = item.eventType === "resume";
        const dotColor = isHeld
          ? "#e53e3e"
          : isReleased
            ? "#38a169"
            : "#d4af37";
        return `
        <div class="hist-row">
          <div class="hist-dot" style="background:${dotColor}"></div>
          <div class="hist-body">
            <div class="hist-top">
              <span class="hist-status"${isHeld ? ' style="color:#e53e3e"' : ""}>${isHeld ? "HELD" : item.status}</span>
              <span class="hist-time">${item.timestamp ? formatDateTime(item.timestamp) : ""}</span>
            </div>
            ${item.location ? `<div class="hist-loc">${item.location}</div>` : ""}
            ${item.description ? `<div class="hist-desc">${item.description}</div>` : ""}
            ${item.address ? `<div class="hist-addr${isHeld ? " held" : ""}">${fmtAddr(item.address)}</div>` : ""}
          </div>
        </div>`;
      })
      .join("");

    const printWindow = window.open("", "_blank");
    if (!printWindow) return;

    printWindow.document.write(`<!DOCTYPE html>
<html>
<head>
  <meta charset="UTF-8"/>
  <title>Tracking Report — ${shipment.trackingNumber}</title>
  <script src="https://cdn.jsdelivr.net/npm/jsbarcode@3.11.5/dist/JsBarcode.all.min.js"></script>
  <style>
    * {
      box-sizing: border-box; margin: 0; padding: 0;
      -webkit-print-color-adjust: exact; print-color-adjust: exact;
    }
    @page { size: A4; margin: 12mm; }
    body {
      font-family: 'Segoe UI', Arial, sans-serif;
      color: #1a2433;
      font-size: 12px;
      max-width: 760px;
      margin: 0 auto;
      padding: 16px;
    }

    /* ── Brand header ── */
    .report-header {
      display: flex; justify-content: space-between; align-items: center;
      background: #0b1e33; color: white;
      border-radius: 10px; padding: 16px 20px;
      border-bottom: 4px solid #d4af37;
    }
    .company-name { font-size: 19px; font-weight: 900; letter-spacing: 1px; }
    .company-sub { font-size: 9px; color: #9fb0c3; margin-top: 3px; letter-spacing: 0.5px; }
    .report-label {
      background: #d4af37; color: #0b1e33;
      font-weight: 800; font-size: 10px; letter-spacing: 1.5px;
      padding: 5px 14px; border-radius: 4px; text-transform: uppercase;
    }

    /* ── Barcode ── */
    .barcode-row {
      text-align: center; margin: 14px 0; padding: 10px 0;
      border-top: 1px dashed #d8dee6; border-bottom: 1px dashed #d8dee6;
    }
    .barcode-row svg { display: block; margin: 0 auto; }
    .tracking-text {
      font-family: 'Courier New', monospace;
      font-size: 14px; font-weight: 700; letter-spacing: 2px;
      color: #0b1e33; margin-top: 5px;
    }

    /* ── Status bar ── */
    .status-bar { display: flex; align-items: center; gap: 10px; flex-wrap: wrap; margin-bottom: 12px; }
    .status-pill {
      padding: 5px 14px; border-radius: 20px;
      font-size: 11px; font-weight: 800; letter-spacing: 0.5px;
      color: white; background: ${statusColor};
    }
    .meta-chip {
      font-size: 10px; font-weight: 600; color: #4a5568;
      background: #eef1f5; padding: 4px 10px; border-radius: 12px;
    }
    .meta-chip.gold { background: #faf3dd; color: #8a6d1a; }

    /* ── Held ── */
    .held-banner {
      background: linear-gradient(135deg, #c53030, #9b2c2c); color: white;
      text-align: center; font-weight: 800; letter-spacing: 2px;
      font-size: 13px; text-transform: uppercase;
      padding: 10px; border-radius: 8px; margin-bottom: 8px;
    }
    .held-at {
      background: #fff5f5; border: 1.5px solid #e53e3e; border-radius: 8px;
      padding: 8px 12px; margin-bottom: 12px; font-size: 11px; color: #0b1e33;
    }
    .held-at b {
      color: #e53e3e; text-transform: uppercase; font-size: 9px;
      letter-spacing: 1px; display: block; margin-bottom: 2px;
    }

    /* ── Sections ── */
    .section-title {
      font-size: 10px; font-weight: 800; text-transform: uppercase;
      letter-spacing: 1.2px; color: #0b1e33;
      border-bottom: 2px solid #d4af37;
      padding-bottom: 4px; margin: 14px 0 8px;
    }

    .details-grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 8px 14px; }
    .cell { break-inside: avoid; }
    .cell-label {
      font-size: 8.5px; font-weight: 700; text-transform: uppercase;
      letter-spacing: 0.8px; color: #8a97a8; margin-bottom: 2px;
    }
    .cell-value { font-size: 11.5px; font-weight: 600; color: #0b1e33; }

    /* ── Parties ── */
    .parties { display: grid; grid-template-columns: 1fr 1fr; gap: 12px; }
    .party-box { border: 2px solid #0b1e33; border-radius: 8px; padding: 10px 12px; break-inside: avoid; }
    .party-box.to { border-color: #d4af37; }
    .party-label {
      font-size: 8.5px; font-weight: 800; text-transform: uppercase; letter-spacing: 1.2px;
      color: white; background: #0b1e33;
      padding: 2px 8px; border-radius: 3px; display: inline-block; margin-bottom: 6px;
    }
    .party-box.to .party-label { background: #d4af37; color: #0b1e33; }
    .party-name { font-size: 13px; font-weight: 800; color: #0b1e33; }
    .party-line { font-size: 10.5px; color: #4a5568; margin-top: 2px; line-height: 1.45; }

    /* ── Travel history ── */
    .hist-row {
      display: flex; gap: 10px; padding: 7px 0;
      border-bottom: 1px solid #eef1f5; break-inside: avoid;
    }
    .hist-row:last-child { border-bottom: none; }
    .hist-dot { width: 11px; height: 11px; border-radius: 50%; margin-top: 2px; flex-shrink: 0; }
    .hist-body { flex: 1; }
    .hist-top { display: flex; justify-content: space-between; gap: 10px; }
    .hist-status { font-size: 11px; font-weight: 800; color: #0b1e33; letter-spacing: 0.4px; }
    .hist-time { font-size: 9.5px; color: #8a97a8; white-space: nowrap; }
    .hist-loc { font-size: 10px; font-weight: 700; color: #b8860b; margin-top: 1px; }
    .hist-desc { font-size: 10px; color: #5a6675; margin-top: 1px; }
    .hist-addr {
      font-size: 10px; color: #0b1e33; background: #f4f6f9;
      border-left: 3px solid #d4af37; border-radius: 0 4px 4px 0;
      padding: 4px 8px; margin-top: 4px; display: inline-block;
    }
    .hist-addr.held { background: #fff5f5; border-left-color: #e53e3e; }

    /* ── Footer ── */
    .report-footer {
      text-align: center; font-size: 9px; color: #98a4b3;
      margin-top: 16px; padding-top: 10px;
      border-top: 1px solid #e3e8ee; line-height: 1.6;
    }
    .report-footer b { color: #5a6675; }
  </style>
</head>
<body>
  <div class="report-header">
    <div>
      <div class="company-name">TRANS WORLD SHIPPING CO.</div>
      <div class="company-sub">Global Reach · Local Expertise · Reliable Delivery</div>
    </div>
    <div class="report-label">Tracking Report</div>
  </div>

  <div class="barcode-row">
    <svg id="barcode"></svg>
    <div class="tracking-text">${shipment.trackingNumber}</div>
  </div>

  <div class="status-bar">
    <span class="status-pill">${held ? "HELD" : shipment.status}</span>
    <span class="meta-chip gold">${shipment.serviceType || "Standard"}</span>
    <span class="meta-chip">Carrier: ${shipment.carrier || "Trans World Shipping"}</span>
    ${shipment.isFragile ? '<span class="meta-chip" style="background:#fff0f0;color:#c53030;">FRAGILE</span>' : ""}
    ${shipment.requiresSignature ? '<span class="meta-chip">Signature Required</span>' : ""}
  </div>

  ${
    held
      ? `<div class="held-banner">Shipment has been held</div>
  ${
    shipment.currentAddress
      ? `<div class="held-at"><b>Held at</b>${fmtAddr(shipment.currentAddress)}</div>`
      : ""
  }`
      : ""
  }

  <div class="section-title">Shipment Details</div>
  <div class="details-grid">
    <div class="cell">
      <div class="cell-label">Route</div>
      <div class="cell-value">${formatAddress(shipment.originAddress)} → ${formatAddress(shipment.destinationAddress)}</div>
    </div>
    <div class="cell">
      <div class="cell-label">Sent Date</div>
      <div class="cell-value">${formatDate(shipment.sentDate)}</div>
    </div>
    <div class="cell">
      <div class="cell-label">Estimated Delivery</div>
      <div class="cell-value">${shipment.estimatedDeliveryDate ? formatDate(shipment.estimatedDeliveryDate) : "Pending"}</div>
    </div>
    ${
      shipment.weight?.value
        ? `<div class="cell">
      <div class="cell-label">Weight</div>
      <div class="cell-value">${shipment.weight.value.toLocaleString()} ${shipment.weight.unit}</div>
    </div>`
        : ""
    }
    ${
      shipment.dimensions?.length || shipment.dimensions?.width || shipment.dimensions?.height
        ? `<div class="cell">
      <div class="cell-label">Dimensions</div>
      <div class="cell-value">${shipment.dimensions.length} × ${shipment.dimensions.width} × ${shipment.dimensions.height} ${shipment.dimensions.unit}</div>
    </div>`
        : ""
    }
    <div class="cell">
      <div class="cell-label">Days In Transit (Est.)</div>
      <div class="cell-value">${shipment.estimatedDaysInTransit ? `${shipment.estimatedDaysInTransit} days` : "—"}</div>
    </div>
    ${
      shipment.description
        ? `<div class="cell" style="grid-column: span 3;">
      <div class="cell-label">Description</div>
      <div class="cell-value">${shipment.description}</div>
    </div>`
        : ""
    }
    ${
      shipment.specialInstructions
        ? `<div class="cell" style="grid-column: span 3;">
      <div class="cell-label">Special Instructions</div>
      <div class="cell-value">${shipment.specialInstructions}</div>
    </div>`
        : ""
    }
  </div>

  <div class="section-title">Parties</div>
  <div class="parties">
    <div class="party-box">
      <span class="party-label">Shipped By</span>
      <div class="party-name">${shipment.sender.company || shipment.sender.name}</div>
      ${shipment.sender.company ? `<div class="party-line">Contact: ${shipment.sender.name}</div>` : ""}
      <div class="party-line">${shipment.sender.email}</div>
      <div class="party-line">${shipment.sender.phone}</div>
      <div class="party-line">${fmtAddr(shipment.originAddress)}</div>
    </div>
    <div class="party-box to">
      <span class="party-label">Shipping To</span>
      <div class="party-name">${shipment.receiver.company || shipment.receiver.name}</div>
      ${shipment.receiver.company ? `<div class="party-line">Contact: ${shipment.receiver.name}</div>` : ""}
      <div class="party-line">${shipment.receiver.email}</div>
      <div class="party-line">${shipment.receiver.phone}</div>
      <div class="party-line">${fmtAddr(shipment.destinationAddress)}</div>
    </div>
  </div>

  ${
    shipment.trackingHistory.length > 0
      ? `<div class="section-title">Travel History</div>
  ${historyRows}`
      : ""
  }

  <div class="report-footer">
    <b>Trans World Shipping Company</b> · info@transworldshippingcompany.com · www.transworldshipping.delivery<br/>
    Generated: ${new Date().toLocaleString()} · Tracking Number: ${shipment.trackingNumber}
  </div>

  <script>
    window.onload = function() {
      try {
        JsBarcode("#barcode", "${shipment.trackingNumber}", {
          format: "CODE128",
          width: 2,
          height: 55,
          displayValue: false,
          margin: 4,
          background: "#ffffff",
          lineColor: "#0b1e33"
        });
      } catch (e) {
        var el = document.getElementById("barcode");
        if (el) el.style.display = "none";
      }
      setTimeout(function() {
        window.print();
        window.onafterprint = function() { window.close(); };
      }, 500);
    };
  </script>
</body>
</html>`);
    printWindow.document.close();
  };

  const progress = shipment
    ? shipment.status === "Delivered" || shipment.actualDeliveryDate
      ? 100
      : shipment.isBeingPackaged && !shipment.inTransit
        ? 2
        : shipment.estimatedDeliveryDate
          ? calcProgress(shipment.sentDate, shipment.estimatedDeliveryDate)
          : null // no estimated delivery date on file - can't compute a percentage
    : 0;

  const getProgressColor = () => {
    if (progress === null) return "#8a97a8";
    if (progress >= 75) return "#4CAF50";
    if (progress >= 40) return "#D4AF37";
    return "#2196F3";
  };

  const isPackaging = shipment?.isBeingPackaged && !shipment?.inTransit;
  const isCancelled = shipment?.status === "Cancelled";
  // latest cancellation note from the tracking history (set via the admin's Update Note)
  const cancelNote = isCancelled
    ? [...(shipment?.trackingHistory || [])]
        .reverse()
        .find((e) => e.status === "Cancelled")?.description || ""
    : "";

  return (
    <>
      <Helmet>
        <title>Track Shipment | Trans World Shipping</title>
        <meta
          name="description"
          content="Track your shipment in real-time with Trans World Shipping. Enter your tracking number to get instant updates."
        />
      </Helmet>

      <TheTrackShipment>
        <div className="track-container">
          <div className="track-hero">
            <h1>Track Your Shipment</h1>
            <p>Enter your tracking number to get real-time updates</p>

            <form onSubmit={handleSearch} className="search-form" noValidate>
              <div className="search-wrapper">
                <input
                  type="text"
                  placeholder="Enter tracking number e.g. TSWC-12168440-TRK-52870"
                  value={trackingNumber}
                  onChange={(e) => {
                    setTrackingNumber(e.target.value);
                    if (error) setError("");
                  }}
                />
                <button type="submit" disabled={isTracking}>
                  {isTracking ? (
                    <FaSpinner className="spinning" />
                  ) : (
                    <FaSearch />
                  )}
                  {isTracking ? "Tracking…" : "Track"}
                </button>
              </div>
              {error && <div className="error-message">{error}</div>}
            </form>
          </div>

          {shipment && (
            <div className="shipment-results">
              <div className="print-container">
                {/* Results Header - tracking number, status, print on top */}
                <div className="results-header">
                  <div className="results-id">
                    <span className="results-label">Tracking Number</span>
                    <h2>{shipment.trackingNumber}</h2>
                    <div className="results-meta">
                      <span
                        className="status-chip"
                        style={{
                          backgroundColor:
                            isCancelled || shipment.isPaused
                              ? "#e53e3e"
                              : getProgressColor(),
                        }}
                      >
                        {isCancelled
                          ? "CANCELLED"
                          : shipment.isPaused
                            ? "HELD"
                            : shipment.status}
                      </span>
                      {!isCancelled && (
                        <span className="est-delivery">
                          Estimated Delivery:{" "}
                          {shipment.estimatedDeliveryDate
                            ? formatDate(shipment.estimatedDeliveryDate)
                            : "Pending"}
                        </span>
                      )}
                    </div>
                  </div>
                  {!isCancelled && (
                    <button
                      onClick={handlePrint}
                      className="print-btn no-print"
                    >
                      <FaPrint /> Print Details
                    </button>
                  )}
                </div>

                {/* Status Banner */}
                <div
                  className={`status-banner ${
                    isCancelled
                      ? "held"
                      : isPackaging
                        ? "packaging"
                        : shipment.isPaused
                          ? "held"
                          : "shipped"
                  }`}
                >
                  {isCancelled ? (
                    <div className="cancelled-block">
                      <span className="held-text">
                        Shipment has been CANCELLED
                      </span>
                      {cancelNote && (
                        <span className="cancel-note">{cancelNote}</span>
                      )}
                    </div>
                  ) : isPackaging ? (
                    <>
                      <FaBox className="banner-icon" />
                      <span>
                        Goods are being packaged and prepared for shipment
                      </span>
                    </>
                  ) : shipment.isPaused ? (
                    <span className="held-text">Shipment has been HELD</span>
                  ) : (
                    <>
                      <FaTruck className="banner-icon" />
                      <span>
                        {shipment.isDelayed
                          ? `Shipment delayed${shipment.delayReason ? `: ${shipment.delayReason}` : ""}`
                          : "Your shipment is on its way"}
                      </span>
                    </>
                  )}
                </div>

                {/* Held Location Callout (UPS-style) */}
                {shipment.isPaused && !isCancelled && shipment.currentAddress && (
                  <div className="current-location">
                    <FaMapMarkerAlt className="pin-icon" />
                    <div>
                      <span className="current-location-label">Held at</span>
                      <span className="current-location-value">
                        {shipment.currentAddress.street &&
                          `${shipment.currentAddress.street}, `}
                        {shipment.currentAddress.city},{" "}
                        {shipment.currentAddress.state}{" "}
                        {shipment.currentAddress.postalCode},{" "}
                        {shipment.currentAddress.country}
                      </span>
                    </div>
                  </div>
                )}

                {/* Two-column layout: main content + travel history sidebar */}
                <div className="results-grid">
                  <div className="results-main">

                {/* Truck Progress Animation */}
                {!isPackaging && !isCancelled && (
                  <div className="animation-section no-print">
                    {progress === null ? (
                      <div className="estimate-pending">
                        <FaTruck className="banner-icon" />
                        <span>
                          Your shipment is on its way — delivery estimate
                          pending
                        </span>
                      </div>
                    ) : (
                      <>
                        <div className="route-line">
                          <div className="start-point">
                            <FaMapMarkerAlt />
                            <span>
                              {shipment.originAddress.city || "Origin"}
                            </span>
                          </div>
                          <div className="route-path">
                            <div
                              className={`moving-truck ${progress >= 100 ? "completed" : ""}`}
                              style={{ left: `${progress}%` }}
                            >
                              <FaTruck />
                            </div>
                            <div className="progress-bar">
                              <div
                                className="progress-fill"
                                style={{
                                  width: `${progress}%`,
                                  backgroundColor: getProgressColor(),
                                }}
                              />
                            </div>
                          </div>
                          <div className="end-point">
                            <FaMapMarkerAlt />
                            <span>
                              {shipment.destinationAddress.city ||
                                "Destination"}
                            </span>
                          </div>
                        </div>
                        <div className="progress-stats">
                          <span>{progress}% Complete</span>
                          <span>
                            Est. Delivery:{" "}
                            {formatDate(shipment.estimatedDeliveryDate)}
                          </span>
                        </div>
                      </>
                    )}
                  </div>
                )}

                {/* Packaging Animation */}
                {isPackaging && (
                  <div className="packaging-animation no-print">
                    <div className="packaging-box">
                      <FaBoxOpen className="packaging-icon" />
                      <div className="packaging-particles">
                        <span></span>
                        <span></span>
                        <span></span>
                        <span></span>
                      </div>
                    </div>
                    <div className="packaging-message">
                      <p>
                        Your items are being carefully packaged and prepared for
                        shipment
                      </p>
                      <div className="packaging-progress">
                        <div className="packaging-progress-fill" />
                      </div>
                    </div>
                  </div>
                )}

                {/* Shipment Details (hidden for cancelled shipments) */}
                {!isCancelled && (
                <div className="shipment-details print-section">
                  <div className="details-header">
                    <h2>Shipment Details</h2>
                  </div>

                  <div className="details-grid">
                    <div className="detail-item">
                      <span className="detail-label">Tracking Number</span>
                      <span className="detail-value">
                        {shipment.trackingNumber}
                      </span>
                    </div>
                    <div className="detail-item">
                      <span className="detail-label">Status</span>
                      <span
                        className="status-badge"
                        style={{ backgroundColor: getProgressColor() }}
                      >
                        {shipment.status}
                      </span>
                    </div>
                    <div className="detail-item">
                      <span className="detail-label">Carrier</span>
                      <span className="detail-value">{shipment.carrier}</span>
                    </div>
                    <div className="detail-item">
                      <span className="detail-label">Service Type</span>
                      <span className="detail-value">
                        {shipment.serviceType}
                      </span>
                    </div>
                    {shipment.weight?.value ? (
                      <div className="detail-item">
                        <span className="detail-label">Weight</span>
                        <span className="detail-value">
                          {shipment.weight.value.toLocaleString()}{" "}
                          {shipment.weight.unit}
                        </span>
                      </div>
                    ) : null}
                    {shipment.dimensions?.length ||
                    shipment.dimensions?.width ||
                    shipment.dimensions?.height ? (
                      <div className="detail-item">
                        <span className="detail-label">Dimensions</span>
                        <span className="detail-value">
                          {shipment.dimensions.length} ×{" "}
                          {shipment.dimensions.width} ×{" "}
                          {shipment.dimensions.height}{" "}
                          {shipment.dimensions.unit}
                        </span>
                      </div>
                    ) : null}
                    <div className="detail-item">
                      <span className="detail-label">Sent Date</span>
                      <span className="detail-value">
                        {formatDate(shipment.sentDate)}
                      </span>
                    </div>
                    <div className="detail-item">
                      <span className="detail-label">Estimated Delivery</span>
                      <span className="detail-value">
                        {shipment.estimatedDeliveryDate
                          ? formatDate(shipment.estimatedDeliveryDate)
                          : "Pending"}
                      </span>
                    </div>
                    <div className="detail-item">
                      <span className="detail-label">Route</span>
                      <span className="detail-value">
                        {formatAddress(shipment.originAddress)} →{" "}
                        {formatAddress(shipment.destinationAddress)}
                      </span>
                    </div>
                    {shipment.description && (
                      <div className="detail-item">
                        <span className="detail-label">Description</span>
                        <span className="detail-value">
                          {shipment.description}
                        </span>
                      </div>
                    )}
                    {shipment.specialInstructions && (
                      <div className="detail-item">
                        <span className="detail-label">
                          Special Instructions
                        </span>
                        <span className="detail-value">
                          {shipment.specialInstructions}
                        </span>
                      </div>
                    )}
                    <div className="detail-item">
                      <span className="detail-label">Flags</span>
                      <span className="detail-value flags">
                        {shipment.isFragile && (
                          <span className="flag fragile">Fragile</span>
                        )}
                        {shipment.requiresSignature && (
                          <span className="flag signature">
                            Signature Required
                          </span>
                        )}
                      </span>
                    </div>
                  </div>
                </div>
                )}

                {/* Sender & Receiver */}
                <div className="parties-section print-section">
                  <div className="parties-grid">
                    <div className="party-card">
                      <div className="party-header">
                        {shipment.sender.company ? <FaBuilding /> : <FaUser />}
                        <h3>Shipped By</h3>
                      </div>
                      <div className="party-content">
                        {shipment.sender.company && (
                          <p className="party-name">
                            {shipment.sender.company}
                          </p>
                        )}
                        <p className="party-contact">
                          Contact: {shipment.sender.name}
                        </p>
                        <p className="party-email">{shipment.sender.email}</p>
                        <p className="party-phone">{shipment.sender.phone}</p>
                        <p className="party-address">
                          {shipment.originAddress.street},{" "}
                          {shipment.originAddress.city},{" "}
                          {shipment.originAddress.state}{" "}
                          {shipment.originAddress.postalCode},{" "}
                          {shipment.originAddress.country}
                        </p>
                      </div>
                    </div>

                    <div className="party-card">
                      <div className="party-header">
                        {shipment.receiver.company ? (
                          <FaBuilding />
                        ) : (
                          <FaUser />
                        )}
                        <h3>Shipping To</h3>
                      </div>
                      <div className="party-content">
                        {shipment.receiver.company && (
                          <p className="party-name">
                            {shipment.receiver.company}
                          </p>
                        )}
                        <p className="party-contact">
                          Contact: {shipment.receiver.name}
                        </p>
                        <p className="party-email">{shipment.receiver.email}</p>
                        <p className="party-phone">{shipment.receiver.phone}</p>
                        <p className="party-address">
                          {shipment.destinationAddress.street},{" "}
                          {shipment.destinationAddress.city},{" "}
                          {shipment.destinationAddress.state}{" "}
                          {shipment.destinationAddress.postalCode},{" "}
                          {shipment.destinationAddress.country}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>

                  </div>

                  {/* Travel History Sidebar (right column on large screens) */}
                  {shipment.trackingHistory.length > 0 && (
                    <aside className="results-side">
                      <div className="history-section print-section">
                        <h2>Travel History</h2>
                        <div className="history-list">
                          {[...shipment.trackingHistory]
                            .reverse()
                            .map((item) => {
                              const isHeld = item.eventType === "pause";
                              const isReleased = item.eventType === "resume";
                              return (
                                <div
                                  className={`history-item ${isHeld ? "held" : ""} ${isReleased ? "released" : ""}`}
                                  key={item._id}
                                >
                                  <div className="history-dot">
                                    {isReleased && <FaPlayCircle />}
                                  </div>
                                  <div className="history-content">
                                    <div className="history-top">
                                      <span
                                        className={`history-status ${isHeld ? "held-label" : ""}`}
                                      >
                                        {isHeld ? "HELD" : item.status}
                                      </span>
                                      <span className="history-time">
                                        {formatDateTime(item.timestamp)}
                                      </span>
                                    </div>
                                    <span className="history-location">
                                      {item.location}
                                    </span>
                                    <span className="history-desc">
                                      {item.description}
                                    </span>
                                    {item.address && (
                                      <span className="history-address">
                                        <FaMapMarkerAlt />
                                        {item.address.street &&
                                          `${item.address.street}, `}
                                        {item.address.city},{" "}
                                        {item.address.state}{" "}
                                        {item.address.postalCode},{" "}
                                        {item.address.country}
                                      </span>
                                    )}
                                  </div>
                                </div>
                              );
                            })}
                        </div>
                      </div>
                    </aside>
                  )}
                </div>

              </div>
            </div>
          )}
        </div>
      </TheTrackShipment>
    </>
  );
};

/* ─── Styles ──────────────────────────────────────────────────────────────── */
const TheTrackShipment = styled.div`
  width: 100%;
  min-height: 100vh;
  background: linear-gradient(135deg, #f5f7fa 0%, #e9edf2 100%);

  .track-container {
    width: 90%;
    margin: 0 auto;
    padding: 3rem 0;
  }

  /* ── Results Header (tracking no + status + print, on top) ────────────── */
  .results-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 1.5rem;
    flex-wrap: wrap;
    background: white;
    border-radius: 16px;
    padding: 1.5rem 2rem;
    margin-bottom: 1.5rem;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
    border-top: 4px solid #d4af37;

    .results-id {
      display: flex;
      flex-direction: column;
      gap: 0.35rem;
      min-width: 0;
    }

    .results-label {
      font-size: 0.72rem;
      font-weight: 700;
      color: #a0aec0;
      text-transform: uppercase;
      letter-spacing: 0.08em;
    }

    h2 {
      font-family: "JetBrains Mono", "Courier New", monospace;
      font-size: 1.5rem;
      font-weight: 700;
      color: #0b1e33;
      letter-spacing: 0.04em;
      margin: 0;
      word-break: break-all;
    }

    .results-meta {
      display: flex;
      align-items: center;
      gap: 1rem;
      flex-wrap: wrap;
      margin-top: 0.25rem;
    }

    .status-chip {
      display: inline-block;
      padding: 0.3rem 1rem;
      border-radius: 20px;
      color: white;
      font-size: 0.78rem;
      font-weight: 700;
      letter-spacing: 0.04em;
    }

    .est-delivery {
      font-size: 0.85rem;
      color: #718096;
    }

    .print-btn {
      display: inline-flex;
      align-items: center;
      gap: 0.5rem;
      padding: 0.75rem 1.6rem;
      background: #0b1e33;
      color: white;
      border: none;
      border-radius: 10px;
      font-size: 0.9rem;
      font-weight: 600;
      font-family: inherit;
      cursor: pointer;
      transition: all 0.25s ease;
      flex-shrink: 0;

      svg {
        color: #d4af37;
      }

      &:hover {
        background: #d4af37;
        color: #0b1e33;
        transform: translateY(-2px);
        box-shadow: 0 6px 16px rgba(212, 175, 55, 0.35);

        svg {
          color: #0b1e33;
        }
      }
    }
  }

  /* ── Results two-column grid ──────────────────────────────────────────── */
  .results-grid {
    display: grid;
    grid-template-columns: minmax(0, 1fr) 400px;
    gap: 1.5rem;
    align-items: start;
  }

  .results-main {
    min-width: 0;
  }

  .results-side {
    min-width: 0;

    .history-section {
      position: sticky;
      top: 100px;
      max-height: calc(100vh - 130px);
      overflow-y: auto;
      margin-bottom: 0;

      &::-webkit-scrollbar {
        width: 6px;
      }

      &::-webkit-scrollbar-thumb {
        background: #cbd5e0;
        border-radius: 3px;
      }
    }
  }

  @media screen and (max-width: 1100px) {
    .results-grid {
      grid-template-columns: 1fr;
    }

    .results-side .history-section {
      position: static;
      max-height: none;
      overflow: visible;
    }
  }

  /* ── Hero ─────────────────────────────────────────────────────────────── */
  .track-hero {
    text-align: center;
    margin-bottom: 3rem;

    h1 {
      font-size: 2.5rem;
      color: #0b1e33;
      margin-bottom: 0.5rem;
      font-weight: 700;
    }

    p {
      font-size: 1rem;
      color: #718096;
      margin-bottom: 2rem;
    }

    .search-form {
      max-width: 640px;
      margin: 0 auto;

      .search-wrapper {
        display: flex;
        gap: 0.5rem;
        background: white;
        border-radius: 50px;
        padding: 0.25rem;
        box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);

        input {
          flex: 1;
          padding: 0.9rem 1.5rem;
          border: none;
          border-radius: 50px;
          font-size: 0.95rem;
          outline: none;
          font-family: inherit;
          color: #0b1e33;

          &::placeholder {
            color: #b0bec5;
          }

          &:focus {
            box-shadow: 0 0 0 2px rgba(212, 175, 55, 0.3);
          }
        }

        button {
          padding: 0.9rem 2rem;
          background: #d4af37;
          border: none;
          border-radius: 50px;
          font-size: 0.95rem;
          font-weight: 600;
          cursor: pointer;
          display: flex;
          align-items: center;
          gap: 0.5rem;
          transition: all 0.3s ease;
          white-space: nowrap;

          &:hover:not(:disabled) {
            background: #c4a12e;
            transform: translateY(-1px);
          }

          &:disabled {
            opacity: 0.7;
            cursor: not-allowed;
          }
        }
      }

      .error-message {
        color: #e53e3e;
        font-size: 0.85rem;
        margin-top: 0.75rem;
        text-align: left;
        padding-left: 1rem;
      }
    }
  }

  /* ── Status Banner ────────────────────────────────────────────────────── */
  .status-banner {
    background: linear-gradient(135deg, #0b1e33 0%, #0a1a2c 100%);
    border-radius: 12px;
    padding: 1rem 1.5rem;
    margin-bottom: 1.5rem;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 0.75rem;
    color: white;

    .banner-icon {
      font-size: 1.3rem;
      color: #d4af37;
    }

    span {
      font-size: 0.95rem;
      font-weight: 500;
    }

    &.packaging {
      background: linear-gradient(135deg, #ff9800 0%, #f57c00 100%);

      .banner-icon {
        color: white;
      }
    }

    &.held {
      background: linear-gradient(135deg, #c53030 0%, #9b2c2c 100%);
      box-shadow: 0 4px 16px rgba(197, 48, 48, 0.35);

      .held-text {
        font-size: 1.1rem;
        font-weight: 800;
        letter-spacing: 0.08em;
        text-transform: uppercase;
      }
    }

    .cancelled-block {
      display: flex;
      flex-direction: column;
      align-items: center;
      gap: 0.35rem;
      text-align: center;

      .cancel-note {
        font-size: 0.88rem;
        font-weight: 500;
        opacity: 0.92;
        letter-spacing: normal;
        text-transform: none;
      }
    }
  }

  /* ── Held Location Callout ────────────────────────────────────────────── */
  .current-location {
    display: flex;
    align-items: center;
    gap: 0.75rem;
    background: #fff5f5;
    border: 1.5px solid #e53e3e;
    border-radius: 12px;
    padding: 0.9rem 1.25rem;
    margin-bottom: 1.5rem;
    box-shadow: 0 4px 12px rgba(229, 62, 62, 0.12);

    .pin-icon {
      font-size: 1.5rem;
      color: #e53e3e;
      flex-shrink: 0;
    }

    div {
      display: flex;
      flex-direction: column;
      gap: 0.15rem;
    }

    .current-location-label {
      font-size: 0.72rem;
      font-weight: 800;
      color: #e53e3e;
      text-transform: uppercase;
      letter-spacing: 0.08em;
    }

    .current-location-value {
      font-size: 0.9rem;
      font-weight: 600;
      color: #0b1e33;
    }
  }

  /* ── Truck Animation ──────────────────────────────────────────────────── */
  .animation-section {
    background: white;
    border-radius: 16px;
    padding: 2rem;
    margin-bottom: 2rem;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);

    .estimate-pending {
      display: flex;
      align-items: center;
      justify-content: center;
      gap: 0.75rem;
      color: #4a5568;
      font-size: 0.95rem;
      text-align: center;
      padding: 0.5rem 0;

      .banner-icon {
        font-size: 1.4rem;
        color: #d4af37;
        flex-shrink: 0;
      }
    }

    .route-line {
      display: flex;
      align-items: center;
      gap: 1rem;
      margin-bottom: 1rem;

      .start-point,
      .end-point {
        display: flex;
        flex-direction: column;
        align-items: center;
        gap: 0.4rem;
        min-width: 70px;

        svg {
          font-size: 1.4rem;
          color: #4caf50;
        }

        span {
          font-size: 0.75rem;
          font-weight: 600;
          color: #0b1e33;
          text-align: center;
        }
      }

      .route-path {
        flex: 1;
        position: relative;
        padding-top: 1.6rem;

        .moving-truck {
          position: absolute;
          top: -4px;
          transform: translateX(-50%);
          z-index: 2;
          transition: left 1s ease;

          svg {
            font-size: 1.8rem;
            color: #d4af37;
            filter: drop-shadow(2px 2px 4px rgba(0, 0, 0, 0.2));
          }

          &.completed svg {
            color: #4caf50;
          }
        }

        .progress-bar {
          height: 6px;
          background: #e2e8f0;
          border-radius: 3px;
          overflow: hidden;

          .progress-fill {
            height: 100%;
            transition: width 1s ease;
            border-radius: 3px;
          }
        }
      }
    }

    .progress-stats {
      display: flex;
      justify-content: space-between;
      margin-top: 0.75rem;
      font-size: 0.85rem;

      span:first-child {
        font-weight: 700;
        color: #d4af37;
      }

      span:last-child {
        color: #718096;
      }
    }
  }

  /* ── Packaging Animation ──────────────────────────────────────────────── */
  .packaging-animation {
    background: white;
    border-radius: 16px;
    padding: 2rem;
    margin-bottom: 2rem;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
    text-align: center;

    .packaging-box {
      position: relative;
      display: inline-block;
      margin-bottom: 1.5rem;

      .packaging-icon {
        font-size: 4rem;
        color: #ff9800;
        animation: bounce 1s ease-in-out infinite;
      }

      .packaging-particles {
        position: absolute;
        inset: 0;

        span {
          position: absolute;
          width: 8px;
          height: 8px;
          background: #ff9800;
          border-radius: 50%;
          opacity: 0;
          animation: particle 1.5s ease-in-out infinite;

          &:nth-child(1) {
            top: -10px;
            left: 20%;
            animation-delay: 0s;
          }
          &:nth-child(2) {
            top: -5px;
            right: 20%;
            animation-delay: 0.3s;
          }
          &:nth-child(3) {
            bottom: -10px;
            left: 30%;
            animation-delay: 0.6s;
          }
          &:nth-child(4) {
            bottom: -5px;
            right: 30%;
            animation-delay: 0.9s;
          }
        }
      }
    }

    .packaging-message {
      p {
        font-size: 1rem;
        color: #0b1e33;
        margin-bottom: 1rem;
        font-weight: 500;
      }

      .packaging-progress {
        width: 100%;
        max-width: 300px;
        margin: 0 auto;
        height: 4px;
        background: #e2e8f0;
        border-radius: 2px;
        overflow: hidden;

        .packaging-progress-fill {
          width: 60%;
          height: 100%;
          background: #ff9800;
          border-radius: 2px;
          animation: progressPulse 2s ease-in-out infinite;
        }
      }
    }
  }

  /* ── Shipment Details ─────────────────────────────────────────────────── */
  .shipment-details {
    background: white;
    border-radius: 16px;
    padding: 1.5rem;
    margin-bottom: 1.5rem;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);

    .details-header {
      display: flex;
      justify-content: space-between;
      align-items: center;
      margin-bottom: 1.5rem;
      padding-bottom: 1rem;
      border-bottom: 2px solid #e2e8f0;

      h2 {
        font-size: 1.3rem;
        color: #0b1e33;
        font-weight: 700;
        margin: 0;
      }
    }

    .details-grid {
      display: grid;
      grid-template-columns: repeat(2, 1fr);
      gap: 1.5rem;

      .detail-item {
        display: flex;
        flex-direction: column;
        gap: 0.25rem;

        .detail-label {
          font-size: 0.72rem;
          color: #718096;
          text-transform: uppercase;
          letter-spacing: 0.06em;
        }

        .detail-value {
          font-size: 0.95rem;
          font-weight: 600;
          color: #0b1e33;

          &.flags {
            display: flex;
            gap: 0.4rem;
            flex-wrap: wrap;
          }
        }

        .flag {
          display: inline-block;
          padding: 0.2rem 0.6rem;
          border-radius: 20px;
          font-size: 0.72rem;
          font-weight: 700;

          &.fragile {
            background: rgba(255, 159, 10, 0.12);
            color: #ff9f0a;
          }

          &.signature {
            background: rgba(33, 150, 243, 0.12);
            color: #2196f3;
          }
        }

        .status-badge {
          display: inline-block;
          padding: 0.3rem 1rem;
          border-radius: 20px;
          color: white;
          font-size: 0.82rem;
          font-weight: 700;
          width: fit-content;
        }
      }
    }
  }

  /* ── Parties ──────────────────────────────────────────────────────────── */
  .parties-section {
    margin-bottom: 1.5rem;

    .parties-grid {
      display: grid;
      grid-template-columns: repeat(2, 1fr);
      gap: 1.5rem;
    }

    .party-card {
      background: white;
      border-radius: 16px;
      padding: 1.5rem;
      box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
      transition:
        transform 0.25s ease,
        box-shadow 0.25s ease;

      &:hover {
        transform: translateY(-3px);
        box-shadow: 0 8px 20px rgba(0, 0, 0, 0.12);
      }

      .party-header {
        display: flex;
        align-items: center;
        gap: 0.75rem;
        margin-bottom: 1rem;
        padding-bottom: 0.75rem;
        border-bottom: 2px solid #e2e8f0;

        svg {
          font-size: 1.3rem;
          color: #d4af37;
        }

        h3 {
          font-size: 1.05rem;
          color: #0b1e33;
          font-weight: 700;
          margin: 0;
        }
      }

      .party-content {
        display: flex;
        flex-direction: column;
        gap: 0.3rem;

        .party-name {
          font-size: 1rem;
          font-weight: 700;
          color: #0b1e33;
        }

        .party-contact,
        .party-email,
        .party-phone,
        .party-address {
          font-size: 0.85rem;
          color: #718096;
          line-height: 1.5;
          margin: 0;
        }
      }
    }
  }

  /* ── Tracking History ─────────────────────────────────────────────────── */
  .history-section {
    background: white;
    border-radius: 16px;
    padding: 1.5rem;
    margin-bottom: 1.5rem;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);

    h2 {
      font-size: 1.2rem;
      color: #0b1e33;
      font-weight: 700;
      margin: 0 0 1.25rem;
      padding-bottom: 0.75rem;
      border-bottom: 2px solid #e2e8f0;
    }

    .history-list {
      display: flex;
      flex-direction: column;
      gap: 0;
    }

    .history-item {
      display: flex;
      align-items: flex-start;
      gap: 1rem;
      position: relative;
      padding-bottom: 1.25rem;

      &:last-child {
        padding-bottom: 0;

        &::after {
          display: none;
        }
      }

      &::after {
        content: "";
        position: absolute;
        left: 7px;
        top: 16px;
        bottom: 0;
        width: 2px;
        background: #e2e8f0;
      }

      .history-dot {
        width: 16px;
        height: 16px;
        border-radius: 50%;
        background: #d4af37;
        flex-shrink: 0;
        margin-top: 3px;
        z-index: 1;
        box-shadow: 0 0 0 3px rgba(212, 175, 55, 0.2);
        display: flex;
        align-items: center;
        justify-content: center;
        color: transparent;
        font-size: 0.65rem;
      }

      &.held .history-dot {
        background: #e53e3e;
        box-shadow: 0 0 0 3px rgba(229, 62, 62, 0.2);
      }

      &.released .history-dot {
        width: 20px;
        height: 20px;
        margin-top: 0;
        background: #38a169;
        box-shadow: 0 0 0 3px rgba(56, 161, 105, 0.2);
        color: white;
      }

      .history-content {
        display: flex;
        flex-direction: column;
        gap: 0.2rem;
        flex: 1;
      }

      .history-status.held-label {
        color: #e53e3e;
        letter-spacing: 0.06em;
      }

      .history-address {
        display: flex;
        align-items: flex-start;
        gap: 0.4rem;
        font-size: 0.8rem;
        color: #0b1e33;
        background: #f7fafc;
        padding: 0.4rem 0.6rem;
        border-radius: 6px;
        margin-top: 0.3rem;
        line-height: 1.4;
        width: fit-content;

        svg {
          color: #718096;
          margin-top: 0.15rem;
          flex-shrink: 0;
        }
      }

      &.held .history-address {
        background: #fff5f5;
        border-left: 3px solid #e53e3e;

        svg {
          color: #e53e3e;
        }
      }

      .history-top {
        display: flex;
        align-items: center;
        justify-content: space-between;
        gap: 1rem;
        flex-wrap: wrap;
      }

      .history-status {
        font-weight: 700;
        font-size: 0.88rem;
        color: #0b1e33;
      }

      .history-time {
        font-size: 0.75rem;
        color: #a0aec0;
      }

      .history-location {
        font-size: 0.82rem;
        color: #d4af37;
        font-weight: 600;
      }

      .history-desc {
        font-size: 0.82rem;
        color: #718096;
      }
    }
  }

  /* ── Animations ───────────────────────────────────────────────────────── */
  .spinning {
    animation: spin 0.8s linear infinite;
  }

  @keyframes spin {
    to {
      transform: rotate(360deg);
    }
  }

  @keyframes bounce {
    0%,
    100% {
      transform: translateY(0);
    }
    50% {
      transform: translateY(-10px);
    }
  }

  @keyframes particle {
    0% {
      opacity: 0;
      transform: scale(0);
    }
    50% {
      opacity: 1;
      transform: scale(1);
    }
    100% {
      opacity: 0;
      transform: scale(0);
    }
  }

  @keyframes progressPulse {
    0%,
    100% {
      width: 60%;
      opacity: 1;
    }
    50% {
      width: 80%;
      opacity: 0.7;
    }
  }

  /* ── Print ────────────────────────────────────────────────────────────── */
  @media print {
    .no-print {
      display: none !important;
    }
    .track-hero {
      display: none !important;
    }
    .print-btn {
      display: none !important;
    }
    .print-container {
      margin: 0;
      padding: 0;
    }
    .results-grid {
      display: block !important;
    }
    .results-side .history-section {
      position: static !important;
      max-height: none !important;
      overflow: visible !important;
      margin-top: 1.5rem;
    }
    .party-card {
      break-inside: avoid;
      page-break-inside: avoid;
    }
  }

  /* ── Responsive ───────────────────────────────────────────────────────── */
  @media screen and (max-width: 900px) {
    .parties-grid {
      grid-template-columns: 1fr !important;
    }
  }

  @media screen and (max-width: 768px) {
    .track-container {
      width: 100%;
      padding: 2rem 1rem;
    }

    .track-hero h1 {
      font-size: 1.8rem;
    }

    .results-header {
      padding: 1.25rem 1rem;

      h2 {
        font-size: 1.15rem;
      }

      .print-btn {
        width: 100%;
        justify-content: center;
      }
    }

    .search-wrapper {
      flex-direction: column;
      border-radius: 12px !important;

      input {
        border-radius: 12px !important;
      }

      button {
        border-radius: 12px !important;
        justify-content: center;
      }
    }

    .route-line {
      flex-direction: column;

      .start-point,
      .end-point {
        flex-direction: row !important;
        width: 100%;
        justify-content: space-between;
      }

      .route-path {
        width: 100%;
      }
    }

    .details-grid {
      grid-template-columns: 1fr !important;
    }

    .details-header {
      flex-direction: column;
      gap: 1rem;
      align-items: flex-start !important;
    }
  }

  @media screen and (max-width: 600px) {
    .track-hero h1 {
      font-size: 1.5rem;
    }

    .animation-section,
    .shipment-details,
    .packaging-animation {
      padding: 1.25rem;
    }
  }

  @media screen and (max-width: 420px) {
    .track-container {
      width: 100%;
      padding: 1.5rem 0.75rem;
    }

    .track-hero h1 {
      font-size: 1.3rem;
    }

    .progress-stats {
      flex-direction: column;
      gap: 0.25rem;
      align-items: flex-start;
    }
  }

  @media screen and (max-width: 350px) {
    .track-hero h1 {
      font-size: 1.2rem;
    }
  }
`;

export default TrackShipment;
